import clsx from 'clsx';
import { forwardRef, useContext, type ReactNode } from 'react';

import {
  _mergeProps,
  type DataTestId,
  type DOMProps,
  type StylingProps,
} from '@dynatrace/strato-components/core';

import { TerminologyTriggerCSS } from './TerminologyOverlay.sty.js';
import { TerminologyOverlayContext } from './TerminologyOverlayContext.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';

//#region Typings
/**
 * @public
 */
export interface TerminologyOverlayTriggerProps
  extends DOMProps,
    StylingProps,
    DataTestId {
  /**
   * Trigger Overlay button must contain children elements.
   */
  children: ReactNode;
}
//#endregion Typings

/**
 * TerminologyOverlay Trigger component, used for rendering trigger content.
 * @public
 */
export const Trigger = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  TerminologyOverlayTriggerProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  } = props;
  const { focusProps, focusClassName, isFocusVisible } = useFocusRing({
    isMinimal: true,
  });
  const overlayTriggerProps = useContext(TerminologyOverlayContext);

  return (
    <button
      data-testid={dataTestId}
      className={clsx(
        consumerClassName,
        focusClassName,
        TerminologyTriggerCSS,
        { 'strato-focus-visible': isFocusVisible },
      )}
      style={{
        textDecoration: isFocusVisible ? 'none' : undefined,
        ...consumerStyle,
      }}
      ref={forwardedRef}
      {..._mergeProps(overlayTriggerProps, focusProps)}
      aria-haspopup="dialog"
    >
      {children}
    </button>
  );
});

(Trigger as typeof Trigger & { displayName: string }).displayName =
  'TerminologyOverlay.Trigger';
